import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, expand, map, reduce } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { BackendPage, LeaseUpsertPayload, PropertyResponseDto, UnitResponseDto } from '../leases/lease.service';

export type TenantStatusApi =
  | 'APPLICANT'
  | 'APPROVED'
  | 'PENDING_LEASE'
  | 'FUTURE'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'REMOVED'
  | 'BLACKLISTED';
export type TenantRoleApi = 'PRIMARY' | 'SECONDARY' | 'GUARANTOR' | 'EMERGENCY' | 'OTHER';
export type TenantOccupancyStatusApi =
  | 'APPLICANT'
  | 'ACTIVE_LEASE'
  | 'FUTURE_MOVE_IN'
  | 'ACTIVE_NO_LEASE'
  | 'GUARANTOR_ON_LEASE'
  | 'GUARANTOR_ONLY'
  | 'OCCUPANT_ON_LEASE'
  | 'EMERGENCY_CONTACT'
  | 'PAST_TENANT'
  | 'BLOCKED'
  | 'UNASSIGNED';
export type TenantAvailableActionApi =
  | 'CREATE_LEASE'
  | 'ATTACH_EXISTING_LEASE'
  | 'VIEW_LEASE'
  | 'INVITE_PORTAL'
  | 'RENEW_LEASE'
  | 'END_LEASE'
  | 'REACTIVATE'
  | 'DELETE'
  | 'DELETE_BLOCKED_BY_LEASE';

export type TenantLeaseSummaryDto = {
  propertyName?: string | null;
  unitName?: string | null;
  leaseId?: string | null;
  unitId?: string | null;
  propertyId?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  contactRole?: string | null;
  /** The lease's monthly rent (what the tenant actually pays). */
  rent?: number | null;
};

export type TenantResponseDto = {
  id: string;
  organizationId?: string | null;
  householdId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  contactPhone?: string | null;
  role?: TenantRoleApi | string | null;
  status?: TenantStatusApi | string | null;
  notes?: string | null;
  currentUnitId?: string | null;
  currentPropertyId?: string | null;
  profileImageUrl?: string | null;
  activeLease?: TenantLeaseSummaryDto | null;
  activeLeases?: TenantLeaseSummaryDto[] | null;
  blockingLease?: TenantLeaseSummaryDto | null;
  occupancyStatus?: TenantOccupancyStatusApi | string | null;
  portalAccessActive?: boolean | null;
  /** True once the tenant's Apex account is linked: their name comes from the account and can't be edited here. */
  nameManagedByAccount?: boolean | null;
  /** Name on the tenant's ID from identity verification; used on leases and signatures. */
  verifiedLegalName?: string | null;
  legalNameVerifiedAt?: string | null;
  availableActions?: TenantAvailableActionApi[] | string[] | null;
  gender?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
  createdBy?: string | null;
  modifiedBy?: string | null;
  version?: number | null;
};

export type TenantRequestDto = {
  firstName: string;
  lastName: string;
  email?: string | null;
  contactPhone?: string | null;
  role?: TenantRoleApi;
  status?: TenantStatusApi;
  householdId?: string | null;
  notes?: string | null;
  currentUnitId?: string | null;
  gender?: string | null;
  inviteToPortal?: boolean | null;
};

export type TenantOnboardingRequestDto = {
  tenant: TenantRequestDto;
  lease?: (Omit<LeaseUpsertPayload, 'primaryTenantId'> & Partial<Pick<LeaseUpsertPayload, 'primaryTenantId'>>) | null;
};

export type LeaseDocumentCategoryApi =
  | 'LEASE_AGREEMENT'
  | 'ADDENDUM'
  | 'NOTICE'
  | 'MOVE_OUT_FORM'
  | 'SUPPORTING_DOC'
  | 'ID_PROOF'
  | 'OTHER';

export type LeaseDocumentUpload = {
  file: File;
  category: LeaseDocumentCategoryApi;
};

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/property-service`;

  browseTenants(orgId: string, params: { page: number; size: number; q: string; tab: string; sort: string; direction: string; includeSummary: boolean }): Observable<{content: TenantResponseDto[]; totalElements: number; number: number; summary: Record<string, number | null> | null}> {
    return this.http.get<{content: TenantResponseDto[]; totalElements: number; number: number; summary: Record<string, number | null> | null}>(`${this.base}/organizations/${orgId}/tenants/browse`, { params, withCredentials: true });
  }

  listTenants(orgId: string): Observable<BackendPage<TenantResponseDto>> {
    const params = new HttpParams().set('size', '100').set('sort', 'lastName,asc');
    return this.http.get<BackendPage<TenantResponseDto>>(`${this.base}/organizations/${orgId}/tenants`, {
      params,
      withCredentials: true,
    });
  }

  listAllTenants(orgId: string): Observable<TenantResponseDto[]> {
    const size = 100;
    const requestPage = (page: number) => {
      const params = new HttpParams()
        .set('page', String(page))
        .set('size', String(size))
        .set('sort', 'lastName,asc');
      return this.http.get<BackendPage<TenantResponseDto>>(`${this.base}/organizations/${orgId}/tenants`, {
        params,
        withCredentials: true,
      });
    };

    return requestPage(0).pipe(
      expand((page) => {
        const nextPage = (page.number ?? 0) + 1;
        const totalPages = page.totalPages ?? nextPage;
        return nextPage >= totalPages ? [] : requestPage(nextPage);
      }),
      map((page) => page.content ?? []),
      reduce((all, pageContent) => [...all, ...pageContent], [] as TenantResponseDto[]),
    );
  }

  getTenant(orgId: string, tenantId: string): Observable<TenantResponseDto> {
    return this.http.get<TenantResponseDto>(`${this.base}/organizations/${orgId}/tenants/${tenantId}`, {
      withCredentials: true,
    });
  }

  createTenant(orgId: string, payload: TenantRequestDto): Observable<TenantResponseDto> {
    return this.http.post<TenantResponseDto>(`${this.base}/organizations/${orgId}/tenants`, payload, {
      withCredentials: true,
    });
  }

  onboardTenant(orgId: string, payload: TenantOnboardingRequestDto): Observable<TenantResponseDto> {
    return this.http.post<TenantResponseDto>(`${this.base}/organizations/${orgId}/tenant-onboarding`, payload, {
      withCredentials: true,
    });
  }

  onboardTenantWithDocuments(
    orgId: string,
    payload: TenantOnboardingRequestDto,
    leaseDocuments: LeaseDocumentUpload[],
  ): Observable<TenantResponseDto> {
    const formData = new FormData();
    formData.append('tenant', this.toJsonPart('tenant', payload.tenant));

    if (payload.lease) {
      formData.append('lease', this.toJsonPart('lease', payload.lease));
    }

    let currentAgreementKey: string | null = null;
    leaseDocuments.forEach((document, index) => {
      const key = `${document.category.toLowerCase()}__${index}`;
      if (!currentAgreementKey && document.category === 'LEASE_AGREEMENT') {
        currentAgreementKey = key;
      }
      formData.append(key, document.file, document.file.name);
    });

    const params = currentAgreementKey
      ? new HttpParams().set('makeCurrentKey', currentAgreementKey)
      : undefined;

    return this.http.post<TenantResponseDto>(`${this.base}/organizations/${orgId}/tenant-onboarding`, formData, {
      params,
      withCredentials: true,
    });
  }

  private toJsonPart(partName: string, payload: unknown): File {
    const json = JSON.stringify(payload);

    if (!json || json === 'undefined') {
      throw new Error(`Cannot send empty ${partName} payload.`);
    }

    return new File([json], `${partName}.json`, { type: 'application/json' });
  }

  createTenantForProperty(
    orgId: string,
    propertyId: string,
    payload: TenantRequestDto,
  ): Observable<TenantResponseDto> {
    return this.http.post<TenantResponseDto>(
      `${this.base}/organizations/${orgId}/properties/${propertyId}/tenants`,
      payload,
      { withCredentials: true },
    );
  }

  createTenantForUnit(orgId: string, unitId: string, payload: TenantRequestDto): Observable<TenantResponseDto> {
    return this.http.post<TenantResponseDto>(`${this.base}/organizations/${orgId}/units/${unitId}/tenants`, payload, {
      withCredentials: true,
    });
  }

  updateTenant(orgId: string, tenantId: string, payload: Partial<TenantRequestDto>): Observable<TenantResponseDto> {
    return this.http.patch<TenantResponseDto>(`${this.base}/organizations/${orgId}/tenants/${tenantId}`, payload, {
      withCredentials: true,
    });
  }

  resendPortalInvitation(orgId: string, tenantId: string): Observable<TenantResponseDto> {
    return this.http.post<TenantResponseDto>(
      `${this.base}/organizations/${orgId}/tenants/${tenantId}/portal-invitation/resend`,
      {},
      { withCredentials: true },
    );
  }

  deleteTenant(orgId: string, tenantId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/organizations/${orgId}/tenants/${tenantId}`, {
      withCredentials: true,
    });
  }

  listProperties(orgId: string): Observable<BackendPage<PropertyResponseDto>> {
    const params = new HttpParams().set('size', '100').set('sort', 'name,asc');
    return this.http.get<BackendPage<PropertyResponseDto>>(`${this.base}/organizations/${orgId}/properties`, {
      params,
      withCredentials: true,
    });
  }

  listUnits(orgId: string, propertyId: string): Observable<BackendPage<UnitResponseDto>> {
    const params = new HttpParams().set('size', '250').set('sort', 'unitNumber,asc');
    return this.http.get<BackendPage<UnitResponseDto>>(
      `${this.base}/organizations/${orgId}/properties/${propertyId}/units`,
      { params, withCredentials: true },
    );
  }
}
